import Modal from "common/Modal/Modal";
import Text from "common/Typography/Text";
import Flex from "common/Boxes/Flex";
import CurrentUsageValue from "features/settings/Quota/CurrentUsageValue";
import QuotaProgressBar from "features/settings/Quota/QuotaProgressBar";
import { QuotaModalProps } from "@shared/features/settings/types";
import { USAGE_TYPE_TO_ICON } from "features/settings/common/constants";
import PassedQuotaContent from "features/settings/Quota/PassedQuotaContent";
import { useUser } from "@shared/utils/hooks/misc";
import { getUserExpirationInfo } from "@shared/utils/misc";
import { useRestrictedCountriesPurchase } from "utils/hooks/misc";
import {
  BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
  IS_WEB_VIEW,
  PURCHASE_API_SLOTS_SETTINGS_PATH,
} from "common/constants";
import { UsageType } from "@shared/types";
import Button from "common/Buttons/Button";
import { showIntercomSpace } from "utils/misc.tsx";
import Link from "common/Typography/Link.tsx";
import ArrowLeftOutlined from "@ant-design/icons/ArrowLeftOutlined";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import React from "react";

const QuotaModal = ({
  isOpen,
  onCancel,
  title,
  currentUsage,
  usageLimit,
  currentUsageObjectText,
  nextPlanData,
  usageType,
  currentUsageName,
  isSubscriptionExpired,
  okText,
  centered,
  isMaxPlan,
}: QuotaModalProps) => {
  const { user } = useUser();
  const history = useHistory();
  const { onPurchaseClick } = useRestrictedCountriesPurchase();
  const isMaxPlanClientQuota = isMaxPlan && usageType === UsageType.CLIENT;

  const getCurrentUsage = () => {
    if (!usageType) return;
    const Icon = USAGE_TYPE_TO_ICON[usageType];
    return (
      <>
        <Flex style={{ marginBottom: 10 }}>
          <Text
            type="secondary"
            as="div"
            style={{ display: "flex", alignItems: "center" }}
          >
            <Icon style={{ fontSize: 16, marginRight: 8 }} />
            {currentUsageName}
          </Text>
          <CurrentUsageValue
            usageType={usageType}
            currentUsage={currentUsage}
            usageLimit={usageLimit}
          />
        </Flex>
        <QuotaProgressBar percent={100} />
      </>
    );
  };

  const getTopRow = () => {
    if (isSubscriptionExpired) {
      const formattedExpiration = getUserExpirationInfo(user)[1];
      return (
        <Flex style={{ marginBottom: 16 }}>
          <Text style={{ fontWeight: 500 }}>
            Plan - {user?.is_trial ? "Trial - " : ""}
            {user?.package}
          </Text>
          <Text style={{ fontWeight: 500 }} type="danger">
            Expired on {formattedExpiration}
          </Text>
        </Flex>
      );
    } else {
      return getCurrentUsage();
    }
  };

  const getContent = () => {
    if (isSubscriptionExpired) {
      return (
        <Text as="div">
          Before you can perform this action you need to renew your
          subscription.
        </Text>
      );
    } else if (isMaxPlanClientQuota) {
      return (
        <Text as="div">
          You already reached the maximum number of active{" "}
          <Text style={{ fontWeight: 600, display: "inline" }}>
            Primary API Slots.
          </Text>{" "}
          Please delete an unused account or purchase an additional slot by
          contacting our support team.
        </Text>
      );
    }
    return (
      <PassedQuotaContent
        currentUsageObjectText={currentUsageObjectText}
        nextPlanData={nextPlanData}
      />
    );
  };

  const onOk = () => {
    if (isSubscriptionExpired) {
      onPurchaseClick(BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH);
    } else if (isMaxPlanClientQuota) {
      if (IS_WEB_VIEW) {
        showIntercomSpace("home");
      } else {
        history.push(PURCHASE_API_SLOTS_SETTINGS_PATH);
      }
    } else if (isMaxPlan) {
      onCancel();
    }
  };

  const footer = !!(isMaxPlan || isSubscriptionExpired) && (
    <Flex $justifyContent="flex-end">
      {isSubscriptionExpired && (
        <Button onClick={onCancel} style={{ marginRight: 8 }}>
          Cancel
        </Button>
      )}
      {isMaxPlanClientQuota ? (
        <Link onClick={onOk}>
          {okText}
          <Arrow />
        </Link>
      ) : (
        <Button onClick={onOk} type="primary">
          {okText || "OK"}
          <Arrow />
        </Button>
      )}
    </Flex>
  );

  return (
    <Modal
      open={isOpen}
      footer={footer}
      onCancel={onCancel}
      title={title}
      destroyOnHidden={true}
      centered={centered}
    >
      {getTopRow()}
      {getContent()}
    </Modal>
  );
};

export default React.memo(QuotaModal);

const Arrow = styled(ArrowLeftOutlined)`
  transform: rotate(180deg);
  margin-left: 8px;
`;
